import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { AwardEventsService, GlobalsService, SightSchoolService } from "@services";

@Component({
	selector: "learning-path-badge",
	templateUrl: "./learning-path-badge.component.html",
	styleUrls: ["./learning-path-badge.component.css"],
	standalone: false
})
export class LearningPathBadgeComponent implements OnInit, OnDestroy {
	/** Diameter in pixels. Should match the profile picture so they sit side by side. */
	@Input() size: number = 40;

	/** Pixels of overlap with the element to the left (positive = overlap). */
	@Input() overlapPx: number = 12;

	/** If provided, skips the cached + API lookup and shows this award level instead. */
	@Input() awardLevel: string | null = null;

	/** Whether clicking the badge navigates to /sight-school. */
	@Input() linkToSightSchool: boolean = true;

	award: string = "None";
	isLoaded: boolean = false;
	private awardSub: Subscription | null = null;

	constructor(
		private sightSchoolService: SightSchoolService,
		private awardEvents: AwardEventsService,
		private router: Router
	) { }

	async ngOnInit() {
		if (this.awardLevel) {
			this.award = this.awardLevel;
			this.isLoaded = true;
			return;
		}

		// Live-update when sight-school pushes a new award level after a video completes.
		this.awardSub = this.awardEvents.award$.subscribe(level => {
			this.award = level || "None";
			this.isLoaded = true;
		});

		// If the cache is empty (e.g., older session before this rolled out), backfill from the API.
		if (!GlobalsService.userInfo?.learningPathAward) {
			try {
				const fetched = await this.sightSchoolService.getUserAward() || "None";
				this.awardEvents.setAward(fetched);
			} catch { /* leave at "None" */ }
		}
	}

	ngOnDestroy() {
		if (this.awardSub) this.awardSub.unsubscribe();
	}

	get containerStyle(): { [key: string]: string } {
		return {
			width: `${this.size}px`,
			height: `${this.size}px`,
			marginLeft: `-${this.overlapPx}px`
		};
	}

	get iconUrl(): string {
		const level = (this.award || "None").toLowerCase();
		const safe = ["bronze", "silver", "gold", "none"].includes(level) ? level : "none";
		return `assets/learning-paths/${safe}.png`;
	}

	onClick(event: Event) {
		if (!this.linkToSightSchool) return;
		event.stopPropagation();
		this.router.navigateByUrl("/sight-school");
	}
}
